import * as d3 from "d3";
import React, { useEffect } from "react";
import { alternateYPosition, useWindowSize } from "./helpers/utils";

const dragMove = (d, xScale, dataset, callback, setTimelineYear) => {
  const dX = Math.max(
    xScale(new Date(dataset[0].timelineDateAnchor)) - 3, 
    Math.min(xScale(new Date(dataset[dataset.length - 1].timelineDateAnchor)) + 1, d.x)
  )
  const timelineDrag = d3.select('#timeline-drag')
    .classed("dragging", true);
  
  timelineDrag
   .attr('x', dX)

  setTimelineYear(String(xScale.invert(dX).getFullYear() + 1))

  return callback(dX + 3);
}

const dragEnded = () => {
  d3.select('#timeline-drag')
    .classed("dragging", false);
}

const getSvgY = width => {
  let y;
  switch (true) {
    case width > 550:
      y = '-110'
      break;
  
    default:
      y = '-110'
      break;
  }
  return y;
}

const dragIsNotSynced = (timelineDrag, active, xScale) => {
  if(timelineDrag?.attr("class")?.includes('dragging')) return;

  const newAnchor = xScale(new Date(active.timelineDateAnchor)).toString();
  const currentDrag = timelineDrag.attr('x');
  
  return newAnchor !== currentDrag;
}

const Timeline = ({ 
  dataset,
  setActive,
  active,
  setTimelineYear
}) => {
  const windowWidth = useWindowSize();
  
  const margin = { top: 10, right: 10, bottom: 10, left: 10 };
  const yAnchor = 25 / 2;
  
  const xScale = d3
    .scaleTime()
    .domain([
      d3.min(dataset, (d) => new Date(d.timelineDateAnchor)), 
      d3.max(dataset, (d) => new Date(d.timelineDateAnchor))
    ])
    .range([margin.left, 100 - margin.right]); 
  
  const toggleEvents = (dx) => {
    dataset.forEach((dataItem, i) => {
      const nextItem = dataset[i + 1];

      if(!nextItem && xScale(new Date(dataItem.timelineDateAnchor)) < dx) {
        setActive(dataItem);
      }
      
      if(xScale(new Date(dataItem.timelineDateAnchor)) < dx && xScale(new Date(nextItem?.timelineDateAnchor)) > dx){
        setActive(dataItem);
      }
    });
  }

  const setActiveItem = (item) => {    
    d3.select('#timeline-drag')
      .attr('x', xScale(new Date(item.timelineDateAnchor)) - 3)
    setActive(item);
    setTimelineYear(String(new Date(item.timelineDateAnchor).getFullYear() + 1))
  }

  useEffect(() => {
    const drag = d3.drag()
      .subject(function() {
        const slider = d3.select(this);
        return { x: slider.attr('x'), y: slider.attr('y') }
      })
      .on('drag', d => dragMove(d,xScale, dataset, toggleEvents, setTimelineYear))
      .on("end", dragEnded);

    const transition = d3.transition()
      .duration(500)
      .ease(d3.easeLinear);
      
    const timelineDrag = d3.select('#timeline-drag');
    
    if(timelineDrag?.attr('x') === null || dragIsNotSynced(timelineDrag, active, xScale)){
      setTimelineYear(String(new Date(active.timelineDateAnchor).getFullYear() + 1))
      timelineDrag
        .attr('x', xScale(new Date(active.timelineDateAnchor)) - 3)
        .call(drag);
    }

    timelineDrag
      .transition(transition)
      .attr('fill', active?.hexColor)
    
  },[dataset, active])

  return (
    <svg id="timeline-container">
      <AnimatedGradientBackground solidColor={active?.hexColor} />
      <svg 
        viewBox={windowWidth > 767 ? "0 0 200 20" : "2 0 100 20"} 
        id='timeline'
        y={windowWidth <= 767 ? getSvgY(windowWidth) : undefined}
      >
        <rect
          x={xScale(new Date(dataset[0].timelineDateAnchor)) - 3}
          height={0.5}
          width={90}
          y={yAnchor}
          id='timeline-core'
          fill="white"
        />
        {dataset.map((dataItem, i) => {
          const { timelineDateAnchor, companyName } = dataItem;
          const position = i % 2 === 0 ? 'top' : 'bottom';
          const xAnchor = new Date(timelineDateAnchor);
          return (
            <g 
              key={companyName} 
              id={`timeline-item-${i}`} 
              width={100}
              onClick={() => setActiveItem(dataItem)}
            >
              {position === 'top' && (
                <rect
                  x={xScale(xAnchor) - 0.25}
                  height={5}
                  width={0.5}
                  y={yAnchor - 5}
                  fill="white"
                />
              )}
              <circle
                cx={xScale(xAnchor)}
                cy={alternateYPosition(position, yAnchor, 5)}
                r={active.companyName === companyName ? "3.5" : "1.5"}
                className="animated-circle"
                fill="#ffffff66"
              />
              <circle
                cx={xScale(xAnchor)}
                cy={alternateYPosition(position, yAnchor, 5)}
                r="1.5"
                fill="white"
                className="timeline-anchor"
              />
              {position === 'bottom' && (
                <rect
                  x={xScale(xAnchor) - 0.25}
                  height={5}
                  width={0.5}
                  y={yAnchor}
                  fill="white"
                />
              )}
            </g>
          )
        })}
        <rect
          height={'3px'}
          width={'6px'}
          y={yAnchor - 1.5}
          id='timeline-drag'
          stroke="white"
          strokeWidth={0.2}
          rx={0.5}
        >
          <text fill="white"></text>
        </rect>
      </svg>
    </svg>
  );
};

const OPACITY = [
  1,
  0.89,
  0.78,
  0.67,
  0.45,
  0.4,
  0.35,
  0.3,
  0.2
]

const AnimatedGradientBackground = ({ solidColor } ) => {
  useEffect(() => {
    const gradients = d3.selectAll('stop')
    
    let i = 0
    for(const gradient of gradients){
      const t = d3.transition()
        .duration(500)
        .ease(d3.easeLinear);

      d3.select(gradient)
        .transition(t)
        .attr("stop-color", d3.color(solidColor).copy({ opacity: OPACITY[i] }));
      i = i + 1;
    } 
  },[solidColor])
  
  return (
    <>
      <defs id="gradient-defs">
        <linearGradient id="gradient-bg" x1="0.15" y1="0.15" x2="0.85" y2="0.85">
          <stop offset="27%"/>
          <stop offset="37.4%"/>
          <stop offset="47.8%"/>
          <stop offset="58.2%"/>
          <stop offset="79%"/>
          <stop offset="83.2%"/>
          <stop offset="87.4%"/>
          <stop offset="91.6%"/>
          <stop offset="100%"/>
        </linearGradient>
      </defs>
      <rect fill="url(#gradient-bg)" height="100%" width="100%"/>
    </>
  )
}

export default Timeline;